import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Form, Input, Modal, Select, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { ApiError } from '../../api/types';
import { useEvents, useSaveEvent } from './queries';
import { EventFilters, EventInput, EventSortField, GameEvent } from './types';
import { Halloween2026Tab } from './Halloween2026Tab';
import { formatDateTime } from '../../utils/format';

const timezone = 'UTC';
const defaults: EventFilters = { sort_by: 'start_date', sort_order: 'desc' };
const errorText = (error: unknown) => error instanceof ApiError
  ? `${error.code ? `[${error.code}] ` : ''}${error.message}`
  : error instanceof Error ? error.message : 'Request failed';

function utcDateTime(value: string): string {
  return new Date(value).toISOString().slice(0, 19);
}

function filterTimestamp(value: string, nextDay = false): string | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  if (nextDay) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString();
}

interface EventForm extends EventInput { slug: string }

function EventModal({ event, onClose }: { event: GameEvent | null; onClose: () => void }) {
  const [form] = Form.useForm<EventForm>();
  const [error, setError] = useState('');
  const save = useSaveEvent();
  const { message } = App.useApp();
  const submit = async () => {
    setError('');
    try {
      const values = await form.validateFields();
      const input: EventInput = {
        event_name: values.event_name.trim(),
        is_active: values.is_active,
        start_date: new Date(`${values.start_date}Z`).toISOString(),
        end_date: new Date(`${values.end_date}Z`).toISOString(),
        available_until: values.available_until ? new Date(`${values.available_until}Z`).toISOString() : undefined,
      };
      await save.mutateAsync({ id: event?.id, input, slug: values.slug.trim() });
      message.success(event ? 'Event updated' : 'Event created');
      onClose();
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      setError(errorText(err));
    }
  };
  return <Modal title={event ? 'Edit event' : 'Create event'} open onOk={submit} onCancel={onClose} confirmLoading={save.isPending}>
    {error && <Alert type="error" showIcon message={error} className="mb-4" />}
    <Typography.Paragraph type="secondary">Dates and times use {timezone}.</Typography.Paragraph>
    <Form form={form} layout="vertical" disabled={save.isPending} initialValues={event ? {
      ...event, start_date: utcDateTime(event.start_date), end_date: utcDateTime(event.end_date),
      available_until: utcDateTime(event.available_until || event.end_date),
    } : { is_active: false }}>
      <Form.Item name="event_name" label="Name" rules={[{ required: true, whitespace: true }]}><Input /></Form.Item>
      <Form.Item name="slug" label="Slug" extra="The slug cannot be changed after creation." rules={[{ required: true, whitespace: true }, {
        validator: (_, value) => !value || Array.from(value.trim()).length <= 255 ? Promise.resolve() : Promise.reject(new Error('Slug must contain at most 255 characters.')),
      }]}><Input readOnly={!!event} /></Form.Item>
      <Form.Item name="is_active" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
      <Form.Item name="start_date" label="Start date and time" rules={[{ required: true }]}><Input type="datetime-local" step={1} /></Form.Item>
      <Form.Item name="end_date" label="End date and time" dependencies={['start_date']} rules={[{ required: true }, {
        validator: (_, value) => !value || !form.getFieldValue('start_date') || new Date(`${form.getFieldValue('start_date')}Z`) < new Date(`${value}Z`)
          ? Promise.resolve() : Promise.reject(new Error('End date must be after start date.')),
      }]}><Input type="datetime-local" step={1} /></Form.Item>
      <Form.Item name="available_until" label="Available until" dependencies={['end_date']}
        extra={event ? 'Leave blank to keep the current claim-window duration after the end date.' : 'Optional. Halloween 2026 defaults to 3 days after the end date; other events default to the end date.'}
        rules={[{
          validator: (_, value) => !value || !form.getFieldValue('end_date') || new Date(`${value}Z`) >= new Date(`${form.getFieldValue('end_date')}Z`)
            ? Promise.resolve() : Promise.reject(new Error('Available until must be on or after the end date.')),
        }]}><Input type="datetime-local" step={1} /></Form.Item>
    </Form>
  </Modal>;
}

const EventsListTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState(defaults);
  const [modal, setModal] = useState<{ event: GameEvent | null } | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  const invalidRange = !!from && !!to && from > to;
  const filters: EventFilters = {
    ...sort, search: debouncedSearch || undefined,
    is_active: status === 'all' ? undefined : status === 'enabled',
    date_from: invalidRange ? undefined : filterTimestamp(from),
    date_to: invalidRange ? undefined : filterTimestamp(to, true),
  };
  const events = useEvents(filters, !invalidRange);
  const clear = () => {
    setSearch(''); setDebouncedSearch(''); setStatus('all'); setFrom(''); setTo(''); setSort(defaults);
  };
  const sortable = (field: EventSortField) => ({
    key: field, dataIndex: field, sorter: true,
    sortOrder: sort.sort_by === field ? (sort.sort_order === 'asc' ? 'ascend' as const : 'descend' as const) : null,
  });
  const columns: ColumnsType<GameEvent> = [
    { title: 'Name', ...sortable('event_name') },
    { title: 'Slug', ...sortable('slug') },
    { title: 'Enabled', ...sortable('is_active'), render: (value: boolean) => <Tag color={value ? 'green' : 'default'}>{value ? 'Enabled' : 'Disabled'}</Tag> },
    { title: 'Start', ...sortable('start_date'), render: formatDateTime },
    { title: 'End', ...sortable('end_date'), render: formatDateTime },
    { title: 'Available until', key: 'available_until', dataIndex: 'available_until', render: formatDateTime },
    { title: 'Actions', render: (_, row) => <Button type="link" onClick={() => setModal({ event: row })}>Edit</Button> },
  ];
  const onChange: TableProps<GameEvent>['onChange'] = (_, __, sorter) => {
    const selected = Array.isArray(sorter) ? sorter[0] : sorter;
    setSort(selected.order ? { sort_by: selected.columnKey as EventSortField, sort_order: selected.order === 'ascend' ? 'asc' : 'desc' } : defaults);
  };
  return <div>
    <Space wrap className="mb-4">
      <Input placeholder="Search name or slug" aria-label="Search name or slug" allowClear value={search} onChange={e => setSearch(e.target.value)} style={{ width: 240 }} />
      <Select aria-label="Enabled status" value={status} onChange={setStatus} style={{ width: 140 }} options={[{ value: 'all', label: 'All statuses' }, { value: 'enabled', label: 'Enabled' }, { value: 'disabled', label: 'Disabled' }]} />
      <label>From <Input type="date" aria-label="Date range start" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 155 }} /></label>
      <label>Through <Input type="date" aria-label="Date range end" value={to} onChange={e => setTo(e.target.value)} style={{ width: 155 }} /></label>
      <Button onClick={clear}>Clear filters</Button>
      <Button disabled={invalidRange} loading={events.isFetching} onClick={() => events.refetch()}>Refresh</Button>
      <Button type="primary" onClick={() => setModal({ event: null })}>Create event</Button>
    </Space>
    <Typography.Paragraph type="secondary">Times use {timezone}. Date filters include events overlapping the selected days.</Typography.Paragraph>
    {invalidRange && <Alert type="warning" showIcon message="The range end must be on or after its start." className="mb-4" />}
    {events.error && <Alert type="error" showIcon message={errorText(events.error)} className="mb-4" />}
    <Table rowKey="id" columns={columns} dataSource={invalidRange ? [] : events.data || []} loading={!invalidRange && events.isLoading} pagination={false} onChange={onChange} scroll={{ x: 1300 }} />
    {modal && <EventModal event={modal.event} onClose={() => setModal(null)} />}
  </div>;
};

export const EventsPage: React.FC = () => <Tabs items={[
  { key: 'events', label: 'Events', children: <EventsListTab /> },
  { key: 'halloween-2026', label: 'Halloween 2026', children: <Halloween2026Tab /> },
]} />;
